import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Brain, Globe2, Headphones, HeartPulse, Lock, Pill, ShieldCheck } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { ReviewsSection } from "../components/ReviewsSection";
import { ProductCard } from "../components/ProductCard";
import { getProducts, getPosts, getPost, submitEnquiry } from "../lib/api";
import { useEffect, useState } from "react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/114e22c2-b272-445b-b188-ff1dceeb5e07/images/bf36040e8f4e7217c415fffbe3c26b07e90703f90350c9a2dda86101c38aeaaa.jpeg";

const TRUST = [
  { icon: ShieldCheck, title: "Quality Medicines", desc: "Verified catalogue details & genuine standards." },
  { icon: Lock, title: "Secure Ordering", desc: "Protected communication & straightforward enquiries." },
  { icon: Globe2, title: "Worldwide Delivery", desc: "Responsible handling & dispatch information." },
];

const CATEGORIES = [
  { slug: "mens-health", name: "Men's Health", desc: "Product information and support for eligible customers.", icon: HeartPulse, span: "md:col-span-2", featured: true },
  { slug: "focus-sleep", name: "Focus & Sleep Health", desc: "Clear information and customer assistance.", icon: Brain, span: "md:col-span-1", featured: false },
  { slug: "pain-management", name: "Pain Management", desc: "Information for customers seeking appropriate care.", icon: ActivityIcon, span: "md:col-span-1", featured: false },
  { slug: "customer-support", name: "Customer Support", desc: "Help with product questions and order enquiries.", icon: Headphones, span: "md:col-span-2", featured: true },
  { slug: "antibiotics", name: "Antibiotics", desc: "Verified antibiotic listings with clear pack sizes and displayed prices.", icon: Pill, span: "md:col-span-3", featured: false },
];

function ActivityIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={props.className}>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
}

const WHY = [
  { n: "01", title: "Clear product details", desc: "Transparent displayed prices, pack sizes and strengths on every item." },
  { n: "02", title: "A straightforward enquiry experience", desc: "Add products to your basket and checkout flows you into a ready-made enquiry." },
  { n: "03", title: "Customer support by email", desc: "Our team answers product and order questions directly at info@onecarelife.health." },
  { n: "04", title: "Responsible handling", desc: "Healthcare-related enquiries are handled lawfully, with eligibility confirmed per market." },
];

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.2 + i * 0.16, ease: [0.22, 1, 0.36, 1] } }),
};

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "OneCare Pharmacy",
  url: "https://onecarelife.health/",
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
  }, []);

  return (
    <main>
      <Seo
        title="OneCare Pharmacy | Online Pharmacy Product Information & Customer Support"
        description="Explore OneCare Pharmacy product information, displayed prices and customer support. Contact our team for assistance with product and order enquiries."
        path="/"
        jsonLd={jsonLd}
      />

      {/* Kinetic hero */}
      <section ref={heroRef} className="relative overflow-hidden bg-[#093829] text-white">
        <div className="grain absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-24 pt-20 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:pb-32 lg:pt-28">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 font-mono text-xs tracking-wide text-emerald-200"
            >
              Healthcare essentials, with care at every step.
            </motion.span>

            <h1 className="mt-7 font-display text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              <span className="block overflow-hidden pb-1">
                <motion.span custom={0} variants={lineReveal} initial="hidden" animate="show" className="block">
                  Trusted Healthcare.
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span custom={1} variants={lineReveal} initial="hidden" animate="show" className="block text-emerald-300">
                  Delivered Responsibly.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            >
              OneCare Pharmacy is a healthcare-focused online pharmacy designed to make product information,
              customer support and order enquiries easy to navigate. Explore our catalogue, learn about our
              approach to responsible service, or contact our team for assistance.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/products"
                data-testid="hero-explore-catalogue-btn"
                className="group flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-display text-sm font-bold text-[#093829] transition-[background-color,transform] hover:bg-emerald-200 active:scale-95"
              >
                Explore Product Catalogue
                <ArrowRight className="h-4 w-4 transition-[transform] group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-make-enquiry-btn"
                className="rounded-full border border-white/25 px-7 py-3.5 font-display text-sm font-bold text-white transition-[border-color,background-color] hover:border-emerald-300/60 hover:bg-emerald-400/10"
              >
                Make an Enquiry
              </Link>
            </motion.div>
          </div>

          <motion.div style={{ y: imgY }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-[28px] border border-white/10 shadow-2xl shadow-black/40"
            >
              <img src={HERO_IMG} alt="Pharmaceutical tablets and blister packs arranged on a deep emerald surface" className="aspect-[4/3] w-full object-cover" />
            </motion.div>
            <motion.span
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-4 top-8 rounded-xl border border-white/10 bg-[#0D5C46]/90 px-4 py-3 font-mono text-xs text-emerald-100 shadow-lg backdrop-blur-md"
            >
              Quality Medicines
            </motion.span>
            <motion.span
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -right-3 bottom-10 rounded-xl border border-white/10 bg-[#0D5C46]/90 px-4 py-3 font-mono text-xs text-emerald-100 shadow-lg backdrop-blur-md"
            >
              Worldwide Delivery
            </motion.span>
          </motion.div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 sm:grid-cols-3 lg:px-8">
          {TRUST.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.1} className="flex items-start gap-4">
              <span className="rounded-xl bg-[#D8EFE6] p-3 text-emerald-800">
                <t.icon className="h-6 w-6" />
              </span>
              <span>
                <span className="block font-display text-base font-bold text-slate-900">{t.title}</span>
                <span className="mt-1 block text-sm text-slate-500">{t.desc}</span>
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      {/* Categories bento */}
      <section className="bg-[#F8FAFC] py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold tracking-[0.22em] text-emerald-700">SHOP BY CATEGORY</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Find the information you need
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.08} className={c.span}>
                <Link
                  to="/products"
                  data-testid={`category-card-${c.slug}`}
                  className={`group flex h-full min-h-[190px] flex-col justify-between rounded-2xl border p-7 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-900/10 ${
                    c.featured
                      ? "border-[#0D5C46] bg-[#0D5C46] text-white"
                      : "border-slate-200 bg-white text-slate-900"
                  }`}
                >
                  <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${c.featured ? "bg-white/10 text-emerald-200" : "bg-[#D8EFE6] text-emerald-800"}`}>
                    <c.icon className="h-6 w-6" />
                  </span>
                  <span className="mt-6">
                    <span className="block font-display text-xl font-bold">{c.name}</span>
                    <span className={`mt-1 block text-sm ${c.featured ? "text-emerald-100/80" : "text-slate-500"}`}>{c.desc}</span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="border-y border-slate-200 bg-white py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-xs font-semibold tracking-[0.22em] text-emerald-700">FROM THE CATALOGUE</p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                Popular products & displayed prices
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="home-view-all-products-link"
              className="group flex items-center gap-2 font-display text-sm font-bold text-emerald-800 transition-[color] hover:text-emerald-600"
            >
              View all products
              <ArrowRight className="h-4 w-4 transition-[transform] group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {products.slice(0, 4).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Why OneCare */}
      <section className="bg-[#F8FAFC] py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold tracking-[0.22em] text-emerald-700">WHY ONECARE</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Responsible service, built around you
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {WHY.map((w, i) => (
              <Reveal key={w.n} delay={i * 0.08}>
                <div
                  data-testid={`why-card-${w.n}`}
                  className={`flex h-full gap-6 rounded-2xl border p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg ${
                    i === 0 ? "border-[#0D5C46] bg-[#0D5C46] text-white" : "border-slate-200 bg-white"
                  }`}
                >
                  <span className={`font-mono text-sm font-bold ${i === 0 ? "text-emerald-300" : "text-emerald-700"}`}>{w.n}</span>
                  <span>
                    <span className="block font-display text-lg font-bold">{w.title}</span>
                    <span className={`mt-2 block text-sm leading-relaxed ${i === 0 ? "text-emerald-100/85" : "text-slate-500"}`}>{w.desc}</span>
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ReviewsSection />

      {/* CTA band */}
      <section className="bg-[#093829] py-20">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 lg:flex-row lg:items-center lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Have a question about a product?
            </h2>
            <p className="mt-3 max-w-xl text-slate-300">
              Send us an enquiry — our team responds by email as soon as possible.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="cta-make-enquiry-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-display text-sm font-bold text-[#093829] transition-[background-color,transform] hover:bg-emerald-200 active:scale-95"
            >
              Make an Enquiry
              <ArrowRight className="h-4 w-4 transition-[transform] group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
